import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { ORGANISATION_KINDS, type Biller, type OrganisationKind } from '../../api/types'
import { useT } from '../../i18n'
import { Button, Modal, StatusBadge, TextField } from '../../ui'
import screen from '../screen.module.css'
import { truncateGatewayNames } from './gatewayNames'
import { SlicesSection } from './SlicesSection'

export interface BillerFormValues {
  readonly biller_id: string
  readonly name: string
  readonly first_user_email: string
  readonly first_name: string
  readonly last_name: string
  /** Fixed at creation (FR-40); shown read-only when editing. */
  readonly kind: OrganisationKind
  /** "HH:MM" or empty — the settlement cut-off (FR-41). */
  readonly settlement_day_start: string
}

/** 24-hour HH:MM. The hyphen-free class needs no `v`-flag escaping. */
const CUT_OFF_PATTERN = '([01][0-9]|2[0-3]):[0-5][0-9]'

export interface BillerFormModalProps {
  readonly open: boolean
  /** Absent ⇒ creating. Present ⇒ editing that biller. */
  readonly biller?: Biller
  readonly busy: boolean
  readonly onClose: () => void
  readonly onSubmit: (values: BillerFormValues) => void
}

const EMPTY: BillerFormValues = {
  biller_id: '', name: '', first_user_email: '', first_name: '', last_name: '', kind: 'provider', settlement_day_start: '',
}

/**
 * One form for both creating and editing, because they differ in exactly two
 * fields and splitting them would duplicate the seeding logic below.
 *
 * The biller id and the administrator e-mail appear only when creating. The id
 * is the account's permanent key and is referenced by every gateway
 * assignment; the e-mail creates a Keycloak account, and "editing" it here
 * would silently mean "create a second administrator", which is not what the
 * word edit promises. Both are managed from the Users screen afterwards.
 *
 * Gateways are deliberately NOT edited here. With ~200 gateways on the
 * platform a checkbox list was a wall of mostly-disabled rows, so the form
 * shows a read-only summary and deep-links to the Gateways screen — the one
 * editor of that relationship, built to scale (server-side search, unmapped
 * queue, per-row assignment). It also means saving this form can never
 * rewrite assignments made concurrently over there.
 */
export function BillerFormModal({ open, biller, busy, onClose, onSubmit }: BillerFormModalProps) {
  const t = useT()
  const editing = biller !== undefined

  const initial = useMemo<BillerFormValues>(
    () =>
      biller
        ? { ...EMPTY, biller_id: biller.biller_id, name: biller.name, kind: biller.kind, settlement_day_start: biller.settlement_day_start ?? '' }
        : EMPTY,
    [biller],
  )
  const [values, setValues] = useState<BillerFormValues>(initial)

  // Re-seeded DURING RENDER when the modal opens, or opens on a different
  // biller, rather than from an effect. A setState in an effect commits the
  // stale render first and then renders again, so the user sees one frame of
  // the previous biller's name under the new one's heading. The signature is
  // compared rather than the object, because `initial` is a fresh object every
  // render and an identity check would never terminate.
  //
  // Keyed on `open` too, so cancelling and reopening discards a half-typed
  // draft instead of resurrecting it.
  const signature = `${open ? 'open' : 'closed'}:${biller?.biller_id ?? 'new'}`
  const [seededFrom, setSeededFrom] = useState(signature)
  if (seededFrom !== signature) {
    setSeededFrom(signature)
    setValues(initial)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={editing ? t('billers.edit.title', { name: biller.name }) : t('billers.new.title')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="biller-form" loading={busy}>
            {editing ? t('billers.edit.submit') : t('billers.new.submit')}
          </Button>
        </>
      }
    >
      <form
        id="biller-form"
        className={screen.form}
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit({
            ...values,
            biller_id: values.biller_id.trim().toUpperCase(),
            name: values.name.trim(),
            first_user_email: values.first_user_email.trim().toLowerCase(),
          })
        }}
      >
        {!editing && (
          <TextField
            label={t('billers.code')}
            value={values.biller_id}
            required
            maxLength={32}
            // The hyphen is escaped because HTML compiles `pattern` with the
            // `v` flag, under which a bare `-` in a character class is a
            // syntax error — the browser then throws from checkValidity and
            // the field validates NOTHING, silently.
            pattern="[A-Za-z0-9_\-]+"
            description={t('billers.new.codeHint')}
            onChange={(e) => setValues((v) => ({ ...v, biller_id: e.target.value.toUpperCase() }))}
          />
        )}

        <TextField
          label={t('billers.name')}
          value={values.name}
          required
          onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
        />

        {/* The kind decides where the organisation's whole scope comes from
            (gateways or slices), so it is chosen once and never edited: on
            edit it is shown, not offered (FR-40). */}
        {editing ? (
          <p>
            <span className={screen.hint}>{t('billers.kind')}: </span>
            <StatusBadge tone={biller.kind === 'beneficiary' ? 'info' : 'neutral'}>{t(`billers.kind.${biller.kind}`)}</StatusBadge>
          </p>
        ) : (
          <fieldset>
            <legend>{t('billers.kind')}</legend>
            {ORGANISATION_KINDS.map((kind) => (
              <label key={kind} className={screen.radio}>
                <input
                  type="radio"
                  name="kind"
                  value={kind}
                  checked={values.kind === kind}
                  onChange={() => setValues((v) => ({ ...v, kind }))}
                />{' '}
                {t(`billers.kind.${kind}`)}
              </label>
            ))}
            <p className={screen.hint}>{t('billers.kindHint')}</p>
          </fieldset>
        )}

        {/* A settlement day is a beneficiary's: it reconciles what a provider's
            switch settled to it by the cut-off. Providers get no field. */}
        {values.kind === 'beneficiary' && (
          <TextField
            label={t('billers.settlementCutOff')}
            value={values.settlement_day_start}
            pattern={CUT_OFF_PATTERN}
            placeholder="18:00"
            description={t('billers.settlementCutOffHint')}
            onChange={(e) => setValues((v) => ({ ...v, settlement_day_start: e.target.value.trim() }))}
          />
        )}

        {!editing && (
          <>
            <TextField
              label={t('billers.new.firstUserEmail')}
              type="email"
              value={values.first_user_email}
              required
              description={t('billers.new.firstUserEmailHint')}
              onChange={(e) => setValues((v) => ({ ...v, first_user_email: e.target.value }))}
            />
            <TextField
              label={t('billers.new.firstName')}
              value={values.first_name}
              required
              onChange={(e) => setValues((v) => ({ ...v, first_name: e.target.value }))}
            />
            <TextField
              label={t('billers.new.lastName')}
              value={values.last_name}
              required
              onChange={(e) => setValues((v) => ({ ...v, last_name: e.target.value }))}
            />
          </>
        )}

        {values.kind === 'beneficiary' ? (
          editing ? (
            <SlicesSection biller={biller} />
          ) : (
            <fieldset>
              <legend>{t('billers.slices')}</legend>
              <p className={screen.hint}>{t('billers.slicesHint')}</p>
              <p className={screen.hint}>{t('billers.slicesAfterCreate')}</p>
            </fieldset>
          )
        ) : (
        <fieldset>
          <legend>{t('billers.gateways')}</legend>
          <p className={screen.hint}>{t('billers.gatewaysHint')}</p>
          {editing ? (
            <>
              <p>
                {biller.gateways.length === 0
                  ? t('billers.gatewaysNoneAssigned')
                  : t('billers.gatewaysAssigned', {
                      names: truncateGatewayNames(biller.gateways.map((g) => g.gateway_name), t),
                    })}
              </p>
              {/* Pre-filtered deep link: the Gateways screen opens showing
                  exactly this biller's rows. Navigation leaves this screen,
                  which closes the modal with it — no onClose needed. */}
              <Link to="/gateways" search={{ biller: biller.biller_id }}>
                {t('billers.manageGateways')}
              </Link>
            </>
          ) : (
            <p className={screen.hint}>{t('billers.gatewaysAfterCreate')}</p>
          )}
        </fieldset>
        )}
      </form>
    </Modal>
  )
}
